import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import { BaseResponse } from "../../../shared/types";
import {
  BenefitCategoriesResponse,
  BenefitPhotoUploadResponse,
  BenefitServiceResponse,
  BenefitServicesResponse,
  GetBenefitServicesParams,
  PartnerBenefitServiceBody,
  RejectBenefitServiceBody,
} from "./types";

const buildServicesQuery = (params: GetBenefitServicesParams) => {
  const qp = new URLSearchParams();
  if (params.status) qp.append("status", params.status);
  if (params.partner !== undefined) {
    qp.append("partner", String(params.partner));
  }
  if (params.categoryId != null) {
    qp.append("categoryId", params.categoryId.toString());
  }
  if (params.discountType) qp.append("discountType", params.discountType);
  if (params.hasPromoCode !== undefined) {
    qp.append("hasPromoCode", String(params.hasPromoCode));
  }
  if (params.search) qp.append("search", params.search);
  if (params.sort) qp.append("sort", params.sort);
  if (params.order) qp.append("order", params.order);
  if (params.pageToken) qp.append("pageToken", params.pageToken);
  if (params.limit != null) qp.append("limit", params.limit.toString());
  const query = qp.toString();
  return `/admin/benefits/services${query ? `?${query}` : ""}`;
};

export const benefitsApi = createApi({
  reducerPath: "benefitsApi",
  baseQuery: baseApiQuery,
  tagTypes: ["BenefitServices", "BenefitService"],
  endpoints: (builder) => ({
    getBenefitCategories: builder.query<BenefitCategoriesResponse, void>({
      query: () => "/benefits/categories",
    }),

    getBenefitServices: builder.query<
      BenefitServicesResponse,
      GetBenefitServicesParams
    >({
      query: (params) => buildServicesQuery(params),
      providesTags: ["BenefitServices"],
    }),

    getBenefitService: builder.query<BenefitServiceResponse, number>({
      query: (id) => `/admin/benefits/services/${id}`,
      providesTags: (_result, _error, id) => [{ type: "BenefitService", id }],
    }),

    createPartnerBenefitService: builder.mutation<
      BenefitServiceResponse,
      PartnerBenefitServiceBody
    >({
      query: (body) => ({
        url: "/admin/benefits/services",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitServices"],
    }),

    updatePartnerBenefitService: builder.mutation<
      BenefitServiceResponse,
      { id: number; body: PartnerBenefitServiceBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/benefits/services/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "BenefitServices",
        { type: "BenefitService", id },
      ],
    }),

    deleteBenefitService: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/benefits/services/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BenefitServices"],
    }),

    uploadBenefitServicePhoto: builder.mutation<
      BenefitPhotoUploadResponse,
      FormData
    >({
      query: (formData) => ({
        url: "/admin/benefits/services/photo",
        method: "POST",
        body: formData,
      }),
    }),

    approveBenefitService: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/benefits/services/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "BenefitServices",
        { type: "BenefitService", id },
      ],
    }),

    rejectBenefitService: builder.mutation<
      BaseResponse,
      { id: number; body: RejectBenefitServiceBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/benefits/services/${id}/reject`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "BenefitServices",
        { type: "BenefitService", id },
      ],
    }),
  }),
});

export const {
  useGetBenefitCategoriesQuery,
  useGetBenefitServicesQuery,
  useGetBenefitServiceQuery,
  useLazyGetBenefitServiceQuery,
  useCreatePartnerBenefitServiceMutation,
  useUpdatePartnerBenefitServiceMutation,
  useDeleteBenefitServiceMutation,
  useUploadBenefitServicePhotoMutation,
  useApproveBenefitServiceMutation,
  useRejectBenefitServiceMutation,
} = benefitsApi;
